import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { registerCurrentDeviceForPush } from './pushNotifications';

export const notificationSettingsDefaults = {
  loggingReminder: false,
  loggingRemindersByChild: {},
  medicationRemindersByChild: {},
};

const getCurrentUserSettingsRef = () => {
  const user = auth.currentUser;
  if (!user) {
    return null;
  }

  return doc(db, 'user_preferences', user.uid);
};

export const loadUserNotificationSettings = async () => {
  const settingsRef = getCurrentUserSettingsRef();
  if (!settingsRef) {
    return notificationSettingsDefaults;
  }

  const snapshot = await getDoc(settingsRef);
  const notificationSettings = snapshot.data()?.notificationSettings || {};
  return {
    ...notificationSettingsDefaults,
    ...notificationSettings,
  };
};

export const saveUserNotificationSetting = async (key, value) => {
  const settingsRef = getCurrentUserSettingsRef();
  if (!settingsRef) {
    throw new Error('You must be signed in to change notification settings.');
  }

  await setDoc(settingsRef, {
    notificationSettings: {
      [key]: Boolean(value),
    },
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

export const isLoggingReminderEnabledForChild = (settings = {}, childId = '') => {
  if (!childId) {
    return false;
  }

  return Boolean(settings.loggingRemindersByChild?.[childId]);
};

export const saveLoggingReminderForChild = async (childId, value) => {
  if (!childId) {
    throw new Error('A child profile is required to change logging reminders.');
  }

  const settingsRef = getCurrentUserSettingsRef();
  if (!settingsRef) {
    throw new Error('You must be signed in to change notification settings.');
  }

  await setDoc(settingsRef, {
    notificationSettings: {
      loggingRemindersByChild: {
        [childId]: Boolean(value),
      },
    },
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

export const enableLoggingReminderNotifications = async (childId = '') => {
  const registration = await registerCurrentDeviceForPush({
    source: 'logging_reminder',
    prompt: true,
  });

  if (!registration.success) {
    return registration;
  }

  if (childId) {
    await saveLoggingReminderForChild(childId, true);
  } else {
    await saveUserNotificationSetting('loggingReminder', true);
  }
  return registration;
};

export const isMedicationReminderEnabledForChild = (settings = {}, childId = '') => (
  Boolean(childId && settings.medicationRemindersByChild?.[childId])
);

export const saveMedicationReminderForChild = async (childId, value) => {
  if (!childId) throw new Error('A child profile is required to change medication reminders.');
  const settingsRef = getCurrentUserSettingsRef();
  if (!settingsRef) throw new Error('You must be signed in to change medication reminders.');
  await setDoc(settingsRef, {
    notificationSettings: {
      medicationRemindersByChild: { [childId]: Boolean(value) },
    },
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

export const enableMedicationReminderNotifications = async (childId) => {
  const registration = await registerCurrentDeviceForPush({
    source: 'medication_reminder',
    prompt: true,
  });
  if (!registration.success) return registration;
  await saveMedicationReminderForChild(childId, true);
  return registration;
};
